import React from 'react';
import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import { Button } from '@oysterjs/ui/button';
import { type VisibilityConfigFieldsFragment, type VisibilityConfigInput } from '~/api/graphql';
import { VisibilityBadge } from './visibility-badge';
import { VisibilitySelector } from './visibility-selector';
import {
  visibilityInputFromValue,
  visibilityValueFromFragment,
  type VisibilitySelectorValue
} from './visibility-selector.utils';
import { useVisibilityEntitlement } from './use-visibility-entitlement';
import { useOptionalCurrentAgencyUser } from '~/components/current-user';

/**
 * Reads the agency-wide disallow-private policy from the current user
 * context. Components combine this with any per-callsite `disallowPrivate`
 * override; the selector hides the PRIVATE radio if either is true. Returns
 * false when there is no provider in scope (e.g. component-level unit tests)
 * so the popover degrades to caller-only control rather than throwing.
 */
const useAgencyDisallowsPrivate = (): boolean =>
  useOptionalCurrentAgencyUser()?.agency?.configuration?.visibility?.disallowPrivate ?? false;

/**
 * Tailwind classes for the visibility popover panel. Mirrors the
 * MENU_PANEL_CLASS pattern in
 * `pkg/.../artifact-focus-html-document-render-button.tsx` so the animation
 * (scale + fade on the data-[closed]/data-[enter]/data-[leave] transitions)
 * is consistent with the Document Style picker the user already knows.
 */
const VISIBILITY_POPOVER_PANEL_CLASS = clsx(
  'z-[100] w-80 origin-top-right rounded-md bg-white p-4 shadow-lg ring-1 ring-black/5',
  'focus:outline-none',
  'transition data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0',
  'data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in',
  'dark:bg-neutral-900 dark:ring-white/10'
);

/**
 * Visibility trigger button (the round badge wrapped in a pressable
 * border). Used by both `VisibilityPopover` (save-on-confirm) and
 * `VisibilityComposeButton` (value/onChange, no save). Inlined here so the
 * two surfaces stay visually identical.
 */
const EyePillTrigger: React.FC<{
  visibility: VisibilityConfigFieldsFragment | null | undefined;
  /** When true, render only the icon (with a hover label). Used by tight
   *  surfaces like the chat compose row where the surrounding context
   *  makes the meaning obvious and the "Public/Private/Shared" text is
   *  visual noise. Defaults to false (icon + text). */
  iconOnly?: boolean;
}> = ({ visibility, iconOnly = false }) => (
  <Headless.PopoverButton
    as="button"
    type="button"
    aria-label="Edit visibility"
    className={clsx(
      // The badge inside draws with text-current, so the trigger has to name a
      // color rather than inherit whichever surface it was dropped onto.
      'inline-flex items-center rounded-full border border-neutral-200 bg-white',
      'text-neutral-700 dark:text-neutral-300',
      'cursor-pointer hover:bg-neutral-100 hover:border-neutral-300 dark:hover:border-white/20',
      'data-[active]:bg-neutral-100 dark:data-[active]:bg-white/10 data-[active]:border-neutral-300 dark:data-[active]:border-white/20',
      'dark:border-neutral-700 dark:bg-neutral-900 dark:hover:bg-neutral-800',
      'transition-colors',
      iconOnly ? 'size-8 justify-center p-0' : ''
    )}
  >
    <VisibilityBadge visibility={visibility} variant={iconOnly ? 'compact' : 'detail'} />
  </Headless.PopoverButton>
);

interface VisibilityPopoverProps {
  /** Current visibility on the object. */
  visibility: VisibilityConfigFieldsFragment | null | undefined;
  /**
   * Called with the new VisibilityConfigInput when the user saves. The caller
   * is responsible for the underlying mutation; this component does not own
   * the network request so it can adapt to differently shaped mutations
   * (workspace/preset/template/form).
   */
  onSave: (input: VisibilityConfigInput) => Promise<void> | void;
  /** Pass through to selector for admin-disallow-private agencies. */
  disallowPrivate?: boolean;
  /** Disabled when the caller cannot edit visibility (eg. read-only viewers). */
  disabled?: boolean;
  /** Set false only for surfaces that intentionally need the text label. */
  iconOnly?: boolean;
  className?: string;
}

/**
 * Save-on-confirm visibility popover. Renders a read-only badge when the
 * caller lacks the VISIBILITY_CONTROL entitlement, or when explicitly
 * disabled. Used on object detail surfaces (workspace sidebar, etc.) where
 * a click should persist the change through `onSave` immediately.
 */
export const VisibilityPopover: React.FC<VisibilityPopoverProps> = ({
  visibility,
  onSave,
  disallowPrivate: callerDisallowPrivate = false,
  disabled = false,
  iconOnly = true,
  className = ''
}) => {
  const visibilityEnabled = useVisibilityEntitlement();
  const disallowPrivate = callerDisallowPrivate || useAgencyDisallowsPrivate();
  const [draft, setDraft] = React.useState<VisibilitySelectorValue>(
    visibilityValueFromFragment(visibility)
  );
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string>();

  // Re-sync the draft when the parent's visibility changes (e.g. after a
  // refetch following Save). Dep array tracks the inner fields rather than
  // the object reference so we don't re-sync on identity-only changes.
  React.useEffect(() => {
    setDraft(visibilityValueFromFragment(visibility));
    setError(undefined);
  }, [visibility?.level, visibility?.userIds, visibility?.teamIds]);

  if (!visibilityEnabled || disabled) {
    return (
      <VisibilityBadge
        visibility={visibility}
        variant={iconOnly ? 'compact' : 'detail'}
        className={clsx(iconOnly && 'text-neutral-700 dark:text-neutral-300', className)}
      />
    );
  }

  return (
    <Headless.Popover className={clsx('relative inline-block', className)}>
      <EyePillTrigger visibility={visibility} iconOnly={iconOnly} />
      <Headless.PopoverPanel
        transition
        anchor={{ to: 'bottom end', gap: 8 }}
        className={VISIBILITY_POPOVER_PANEL_CLASS}
      >
        {({ close }) => (
          <>
            <VisibilitySelector
              value={draft}
              onChange={setDraft}
              disallowPrivate={disallowPrivate}
            />
            {error ? <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p> : null}
            <div className="mt-3 flex items-center justify-end gap-2">
              <Button
                color="white"
                disabled={saving}
                onClick={() => {
                  setDraft(visibilityValueFromFragment(visibility));
                  setError(undefined);
                  close();
                }}
              >
                Cancel
              </Button>
              <Button
                color="sky"
                loading={saving}
                onClick={async () => {
                  setSaving(true);
                  setError(undefined);
                  try {
                    await onSave(visibilityInputFromValue(draft));
                    close();
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to save visibility');
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                Save
              </Button>
            </div>
          </>
        )}
      </Headless.PopoverPanel>
    </Headless.Popover>
  );
};

interface VisibilityComposeButtonProps {
  value: VisibilitySelectorValue;
  onChange: (next: VisibilitySelectorValue) => void;
  /** Pass through to selector for admin-disallow-private agencies. */
  disallowPrivate?: boolean;
  /** When true, render the trigger as an icon-only pill (no "Public" /
   *  "Private" / "Shared" text). Defaults to true to keep create and
   *  edit surfaces quiet; callers can opt into text when needed. */
  iconOnly?: boolean;
  className?: string;
}

/**
 * Value/onChange visibility popover for compose flows (preset create dialog,
 * template upload dialogs, custom-form upload, save-as-template button).
 * Holds no internal draft state — picks straight from caller-owned form
 * state — so closing the popover doesn't discard the user's choice; the
 * eventual form submission persists everything together.
 */
export const VisibilityComposeButton: React.FC<VisibilityComposeButtonProps> = ({
  value,
  onChange,
  disallowPrivate: callerDisallowPrivate = false,
  iconOnly = true,
  className = ''
}) => {
  const visibilityEnabled = useVisibilityEntitlement();
  const disallowPrivate = callerDisallowPrivate || useAgencyDisallowsPrivate();
  if (!visibilityEnabled) {
    return null;
  }

  // The selector reads a VisibilityConfigFieldsFragment shape for the badge
  // glyph; the compose-mode value is the same shape minus __typename, which
  // VisibilityBadge tolerates.
  const badgeShape: VisibilityConfigFieldsFragment = {
    __typename: 'VisibilityConfig',
    level: value.level,
    userIds: value.userIds,
    teamIds: value.teamIds
  };

  return (
    <Headless.Popover className={clsx('relative inline-block', className)}>
      <EyePillTrigger visibility={badgeShape} iconOnly={iconOnly} />
      <Headless.PopoverPanel
        transition
        anchor={{ to: 'bottom end', gap: 8 }}
        className={VISIBILITY_POPOVER_PANEL_CLASS}
      >
        <VisibilitySelector value={value} onChange={onChange} disallowPrivate={disallowPrivate} />
      </Headless.PopoverPanel>
    </Headless.Popover>
  );
};

interface VisibilitySplitButtonProps {
  value: VisibilitySelectorValue;
  onChange: (next: VisibilitySelectorValue) => void;
  /** Pass through to selector for admin-disallow-private agencies. */
  disallowPrivate?: boolean;
  type?: 'button' | 'submit';
  color?: React.ComponentProps<typeof Button>['color'];
  loading?: boolean;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  matchDisabledAppearance?: boolean;
  /** Tooltip on the action segment, for saying why it is disabled. */
  title?: string;
  /** Holds the visibility segment shut, naming who sees the object, with the reason as its tooltip. */
  locked?: { label: string; reason: string };
  children: React.ReactNode;
  className?: string;
}

const splitButtonLeftClass =
  'rounded-l-lg rounded-r-none border-r-0 before:rounded-l-[calc(theme(borderRadius.lg)-1px)] before:rounded-r-none after:rounded-l-[calc(theme(borderRadius.lg)-1px)] after:rounded-r-none dark:after:rounded-l-lg dark:after:rounded-r-none';

const splitButtonRightClass =
  'h-full rounded-r-lg rounded-l-none border-l-0 before:rounded-r-[calc(theme(borderRadius.lg)-1px)] before:rounded-l-none after:rounded-r-[calc(theme(borderRadius.lg)-1px)] after:rounded-l-none dark:after:rounded-r-lg dark:after:rounded-l-none';

const splitButtonIconClass = 'min-w-[2.75rem] px-0 sm:min-w-[2.25rem] sm:px-0';

/**
 * Primary action + visibility selector as a split button. Used on create
 * surfaces where the visibility applies to the object about to be created;
 * the right-side eye segment opens the same compose-mode selector without
 * adding a separate pill elsewhere in the layout.
 */
export const VisibilitySplitButton: React.FC<VisibilitySplitButtonProps> = ({
  value,
  onChange,
  disallowPrivate: callerDisallowPrivate = false,
  type = 'button',
  color = 'sky',
  loading = false,
  disabled = false,
  onClick,
  matchDisabledAppearance = false,
  title,
  locked,
  children,
  className = ''
}) => {
  const visibilityEnabled = useVisibilityEntitlement();
  const disallowPrivate = callerDisallowPrivate || useAgencyDisallowsPrivate();

  if (!visibilityEnabled) {
    return (
      <Button
        type={type}
        color={color}
        loading={loading}
        disabled={disabled}
        onClick={onClick}
        title={title}
        className={className}
      >
        {children}
      </Button>
    );
  }

  const badgeShape: VisibilityConfigFieldsFragment = {
    __typename: 'VisibilityConfig',
    level: value.level,
    userIds: value.userIds,
    teamIds: value.teamIds
  };

  return (
    <div className={clsx('inline-flex rounded-lg shadow-sm', className)}>
      <Button
        type={type}
        color={color}
        loading={loading}
        disabled={disabled}
        onClick={onClick}
        title={title}
        className={splitButtonLeftClass}
      >
        {children}
      </Button>
      <Headless.Popover className="relative z-10 -ml-px flex self-stretch">
        <Headless.PopoverButton
          as={Button}
          type="button"
          color={color}
          disabled={loading || !!locked}
          title={locked?.reason}
          className={clsx(
            splitButtonRightClass,
            !locked && splitButtonIconClass,
            matchDisabledAppearance && 'opacity-50'
          )}
          aria-label={locked ? `Visibility: ${locked.label}` : 'Set visibility'}
        >
          <VisibilityBadge visibility={badgeShape} variant="compact" />
          {locked?.label}
        </Headless.PopoverButton>
        <Headless.PopoverPanel
          transition
          anchor={{ to: 'bottom end', gap: 8 }}
          className={VISIBILITY_POPOVER_PANEL_CLASS}
        >
          <VisibilitySelector value={value} onChange={onChange} disallowPrivate={disallowPrivate} />
        </Headless.PopoverPanel>
      </Headless.Popover>
    </div>
  );
};
